import { useMemo } from 'react'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'

import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  useNavigateWithPersistParams,
  useReselector,
} from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { SIGNUP_PATHS } from '../helpers'
import { TaxTerms } from './TaxTerms'
import { OptInAndSaveCard } from './OptInAndSaveCard'
import { TextColors, TypographyStyles } from '@/styles'
import { Card, CardColors, Form, Text } from '@/ui'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useSaveAnnualTaxFilingFormData } from '@/features/taxes/annual-taxes/helpers'
import {
  ExtensionReason,
  requestExtension,
} from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import { getAnnualTaxFilingForYearSelector } from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import {
  selectCurrentAnnualTaxDetails,
  selectCurrentAnnualTaxYear,
} from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import { useResolveChooserShown } from '../plans/chooserVisibility'
import { getFieldName, makeReqBoolSchema } from '@/base'
import { SignupPageLayout } from '../SignupPageLayout'
import { SignupFooter } from '../SignupFooter'
import { SignupFormFieldsWrapper } from '../SignupFormFieldsWrapper'

const validationSchema = yup.object({
  needPreviousTaxYearReturn: makeReqBoolSchema({
    error: 'Please select Yes or No.',
  }),
  confirmedTerms: yup.boolean().when('needPreviousTaxYearReturn', {
    is: (value: boolean | null) => value !== null,
    then: (schema) =>
      schema
        .required('The terms and conditions must be accepted.')
        .oneOf([true], 'The terms and conditions must be accepted.'),
    otherwise: (schema) => schema,
  }),
})

export const ActiveTaxForm = () => {
  const navigate = useNavigateWithPersistParams()
  const dispatch = useAppDispatch()
  const financialProfile = useReselector(getFinancialProfile)
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)

  // S-corps and group practices skip the Lite vs Essential picker and land on
  // previous-tax-year directly from aboutYourPractice, so Back should return
  // there rather than to the choosePlan / practice-journey step they never saw.
  const resolveChooserShown = useResolveChooserShown()

  const nextPage = SIGNUP_PATHS.payment
  const goBack = async () =>
    navigate(
      (await resolveChooserShown())
        ? SIGNUP_PATHS.choosePlan
        : SIGNUP_PATHS.aboutYourPractice
    )

  const taxFiling = useReselector(
    getAnnualTaxFilingForYearSelector,
    currentTaxYear
  )

  const saveTaxFiling = useSaveAnnualTaxFilingFormData(currentTaxYear)

  const formik = useFormik({
    initialValues: {
      needPreviousTaxYearReturn: taxFiling
        ? taxFiling?.optedOutAt === null
        : null,
      confirmedTerms: undefined,
    },
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: async ({ needPreviousTaxYearReturn, confirmedTerms }) => {
      // If user opts into tax filing, we confirm and create an AnnualTaxFiling with the late joiner flag
      // Users will still need to confirm via TSK, but these fields enable us to confirm a User's tax filing needs
      if (needPreviousTaxYearReturn) {
        const res = await saveTaxFiling({
          year: currentTaxYear,
          isLateJoiner: true,
          isLateJoinerTermsAcceptedAt: confirmedTerms
            ? new Date().toISOString()
            : null,
          lateJoinerInitiallyOptedIn: true,
          optedOutAt: null,
        })

        await requestExtension(ExtensionReason.lateJoiner)(dispatch)

        if (res) {
          navigate(nextPage)
        }
      } else {
        const res = await saveTaxFiling({
          year: currentTaxYear,
          optedOutAt: new Date().toISOString(),
          // used to track when users accepted opt out terms
          lateJoinerInitiallyOptedIn: false,
          isLateJoiner: false,
          isLateJoinerTermsAcceptedAt: confirmedTerms
            ? new Date().toISOString()
            : null,
        })

        if (res) {
          navigate(nextPage)
        }
      }
    },
  })

  const activeDetails = useReselector(selectCurrentAnnualTaxDetails)

  const irsFilingDeadline = useMemo(() => {
    if (financialProfile?.taxEntityType === TAX_ENTITY_TYPES.form_1120_s) {
      if (activeDetails?.form_1120_s_irs_due_date) {
        return convertUtcToLocalDate(activeDetails.form_1120_s_irs_due_date)
      }
    } else {
      if (activeDetails?.form_1040_irs_due_date) {
        return convertUtcToLocalDate(activeDetails.form_1040_irs_due_date)
      }
    }

    return null
  }, [financialProfile?.taxEntityType, activeDetails])

  const form1120sLateJoinerOptInCutoff = convertUtcToLocalDate(
    activeDetails?.form_1120_s_new_user_tax_cutoff_date
  )

  const form1040LateJoinerOptInCutoff = convertUtcToLocalDate(
    activeDetails?.form_1040_new_user_tax_cutoff_date
  )

  const lateJoinerOptInCutoff =
    financialProfile?.taxEntityType === TAX_ENTITY_TYPES.form_1120_s
      ? form1120sLateJoinerOptInCutoff
      : form1040LateJoinerOptInCutoff

  if (!activeDetails) return null

  const { values, submitForm, isSubmitting, isValid } = formik

  return (
    <SignupPageLayout
      maxWidth="wide"
      footer={
        <SignupFooter
          onBack={goBack}
          onContinue={submitForm}
          continueDisabled={!isValid || isSubmitting}
          continueLoading={isSubmitting}
          continueId="btn-signup-previous-year-form"
        />
      }
    >
      <FormikProvider value={formik}>
        <div className="flex flex-col space-y-6">
          <Text
            variant={TypographyStyles.DISPLAY_MD}
            color={TextColors.HEADER}
            className="w-full !mb-4"
          >
            Tell us your {currentTaxYear} tax preparation needs
          </Text>

          <Text className="min-h-fit !mb-8">
            By doing the following, you&apos;re helping Heard set you up for a
            successful {currentTaxYear} tax season.
          </Text>

          <div className="flex flex-col md:flex-row w-full gap-14">
            <SignupFormFieldsWrapper>
              <div className="space-y-6">
                <Text
                  variant={TypographyStyles.H1}
                  color={TextColors.HEADER}
                  className="!mb-4"
                >
                  {currentTaxYear} Tax preparation terms
                </Text>

                <Text className="!mb-4">
                  Heard is here to help you with your {currentTaxYear} taxes,
                  but as the{' '}
                  {irsFilingDeadline?.toFormat(
                    DATE_FORMATS_LUXON.MONTH_DAY_LONG
                  )}{' '}
                  tax deadline approaches, we want to ensure we provide you with
                  clear instructions and expectations for what the process looks
                  like to have us file your taxes.
                </Text>

                <Text className="min-h-fit !mb-8">
                  When you join, we&apos;ll make it easy to keep track of
                  everything you have to do, and by when.
                </Text>

                <TaxTerms
                  taxEntityType={financialProfile?.taxEntityType}
                  currentTaxYear={currentTaxYear}
                />

                <div className="flex flex-col space-y-4 min-h-fit">
                  <div className="flex flex-col space-y-2">
                    <Text
                      variant={TypographyStyles.LABEL_MD}
                      color={TextColors.HEADER}
                    >
                      {`Would you like Heard to prepare your ${currentTaxYear} taxes?`}
                    </Text>
                    <div className="flex gap-2">
                      <Form.Toggle
                        name={getFieldName<typeof values>(
                          'needPreviousTaxYearReturn'
                        )}
                        value
                        variant="outline"
                        size="lg"
                        className="min-w-[72px]"
                      >
                        Yes
                      </Form.Toggle>
                      <Form.Toggle
                        name={getFieldName<typeof values>(
                          'needPreviousTaxYearReturn'
                        )}
                        value={false}
                        variant="outline"
                        size="lg"
                        className="min-w-[72px]"
                      >
                        No
                      </Form.Toggle>
                    </div>
                  </div>

                  {values.needPreviousTaxYearReturn && (
                    <Card
                      color={CardColors.PRIORITY}
                      childrenClassName="flex flex-col gap-4"
                    >
                      <Text>
                        By opting in to {currentTaxYear} taxes, you agree to all
                        of the above terms. You understand that meeting these
                        deadlines is necessary to avoid additional penalties and
                        interest on your tax payment.
                      </Text>

                      <Form.Checkbox
                        label="I have read and agreed to all the terms."
                        className="[&_label]:text-sm [&_label]:text-neutral-700"
                        name={getFieldName<typeof values>('confirmedTerms')}
                      />
                    </Card>
                  )}

                  {values.needPreviousTaxYearReturn === false && (
                    <Card
                      color={CardColors.PRIORITY}
                      childrenClassName="flex flex-col gap-4"
                    >
                      <Text>
                        By opting out, you understand that Heard will not do
                        your personal or business taxes for {currentTaxYear}. If
                        you want to opt back in later, you can do so through the
                        Heard app until{' '}
                        {lateJoinerOptInCutoff?.toFormat(
                          DATE_FORMATS_LUXON.MONTH_DAY_LONG
                        )}
                        .
                      </Text>

                      <Form.Checkbox
                        label="I have read and agreed to all the terms."
                        className="[&_label]:text-sm [&_label]:text-neutral-700"
                        name={getFieldName<typeof values>('confirmedTerms')}
                      />
                    </Card>
                  )}
                </div>
              </div>
            </SignupFormFieldsWrapper>

            <div className="w-[300px] shrink-0 self-start">
              <OptInAndSaveCard />
            </div>
          </div>
        </div>
      </FormikProvider>
    </SignupPageLayout>
  )
}
